import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Lock, Search, Plus, Upload, X, Star, FileText, ImageOff, Trash2 } from "lucide-react";
import { ConfirmDelete } from "@/components/admin/ConfirmDelete";
import { ProductShippingPanel } from "@/components/admin/ProductShippingPanel";
import { ProductMerchantPanel } from "@/components/admin/ProductMerchantPanel";
import { AjaxSelect } from "@/components/AjaxSelect";
import { ProductBulkBar } from "@/components/admin/ProductBulkBar";
import { ProductAvailabilityPanel, availabilityFrom } from "@/components/admin/ProductAvailabilityPanel";
import { toast } from "sonner";

export default function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [creating, setCreating] = useState(false);
  const [del, setDel] = useState(null);
  const [flt, setFlt] = useState({ category: "", brand: "", stock: "", seo: "" });
  const [per, setPer] = useState(50);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [opts, setOpts] = useState({ categories: [], brands: [] });
  useEffect(() => {
    Promise.all([api.get("/admin/categories"), api.get("/admin/brands")])
      .then(([c, b]) => { const u = (a) => [...new Set(a.map((x) => x.name).filter(Boolean))]; setOpts({ categories: u(c.data), brands: u(b.data) }); }).catch(() => {});
  }, []);
  const doDelete = async () => {
    try { await api.delete(`/admin/products/${del.id}`); toast.success(`Deleted ${del.sku}`); setDel(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const load = () => {
    setLoading(true);
    const params = { q: q || undefined, paged: true, skip: page * per, limit: per };
    Object.entries(flt).forEach(([k, v]) => { if (v) params[k] = v; });
    return api.get("/admin/products", { params })
      .then((r) => { setProducts(r.data.items); setTotal(r.data.total); }).finally(() => setLoading(false));
  };
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [q, flt, per, page]); // eslint-disable-line react-hooks/exhaustive-deps
  const setFilter = (k, v) => { setFlt({ ...flt, [k]: v === "all" ? "" : v }); setPage(0); setAllMatching(false); setSel([]); };
  const pages = Math.max(1, Math.ceil(total / per));
  const [sel, setSel] = useState([]);
  const [allMatching, setAllMatching] = useState(false);
  const pageIds = products.map((p) => p.id);
  const pageAll = pageIds.length > 0 && pageIds.every((id) => sel.includes(id));
  const togglePage = () => { setAllMatching(false); setSel(pageAll ? sel.filter((x) => !pageIds.includes(x)) : [...new Set([...sel, ...pageIds])]); };
  const toggleOne = (id) => { setAllMatching(false); setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]); };

  const filtered = products;

  const margin = (p) => {
    const costEx = (p.supplier_gst_treatment || "").startsWith("inclusive") ? p.supplier_cost / 1.15 : p.supplier_cost;
    return p.selling_ex_gst - costEx;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-3xl font-extrabold">Products</h1>
        <Button data-testid="add-product-btn" onClick={() => setCreating(true)} className="bg-accent text-steel hover:bg-amber-600 font-semibold">
          <Plus size={16} className="mr-1" /> Add Product
        </Button>
      </div>
      <p className="text-muted-foreground mb-6">Fully editable inventory. Supplier cost, markup &amp; margin are internal only — never shown to customers.</p>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <Input data-testid="admin-product-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); setAllMatching(false); setSel([]); }} placeholder="Search by SKU, name, brand or keyword…" className="pl-9" />
        </div>
        <FilterSelect testId="filter-category" value={flt.category} onChange={(v) => setFilter("category", v)} placeholder="All categories"
          items={opts.categories.map((c) => [c, c])} />
        <FilterSelect testId="filter-brand" value={flt.brand} onChange={(v) => setFilter("brand", v)} placeholder="All brands"
          items={[["__none__", "No brand"], ...opts.brands.map((b) => [b, b])]} />
        <FilterSelect testId="filter-stock" value={flt.stock} onChange={(v) => setFilter("stock", v)} placeholder="Any stock"
          items={[["in", "In stock (6+)"], ["low", "Low stock (1–5)"], ["out", "Out of stock"]]} />
        <FilterSelect testId="filter-seo" value={flt.seo} onChange={(v) => setFilter("seo", v)} placeholder="Any SEO status"
          items={[["synced", "Brand SEO Sync approved"], ["not_synced", "Not synced yet"], ["locked", "SEO locked"], ["missing_meta", "Missing SEO title/meta"]]} />
        {Object.values(flt).some(Boolean) && <Button size="sm" variant="ghost" onClick={() => { setFlt({ category: "", brand: "", stock: "", seo: "" }); setPage(0); setAllMatching(false); setSel([]); }} data-testid="filter-clear"><X size={14} className="mr-1" />Clear filters</Button>}
      </div>
      <Pager page={page} pages={pages} total={total} per={per} loading={loading} setPage={setPage} setPer={(n) => { setPer(n); setPage(0); }} />
      <ProductBulkBar sel={sel} setSel={setSel} allMatching={allMatching} setAllMatching={setAllMatching} pageIds={pageIds} total={total}
        filters={{ q, ...flt }} opts={opts} onDone={load} />
      <div className="rounded-xl border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="p-3 w-8"><input type="checkbox" checked={pageAll || allMatching} onChange={togglePage} disabled={!pageIds.length} title="Select all on this page" data-testid="products-select-page" /></th>
              <th className="p-3">SKU</th><th className="p-3">Product</th><th className="p-3">Brand</th><th className="p-3 text-right">Supp. Cost</th>
              <th className="p-3 text-right">Markup</th><th className="p-3 text-right">Sell (ex GST)</th>
              <th className="p-3 text-right">GST</th><th className="p-3 text-right">Final</th>
              <th className="p-3 text-right">Margin</th><th className="p-3 text-center">Stock</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.map((p) => (
              <tr key={p.id} data-testid={`admin-product-${p.sku}`} className={`hover:bg-slate-50 ${p.active === false ? "opacity-60" : ""}`}>
                <td className="p-3"><input type="checkbox" checked={allMatching || sel.includes(p.id)} onChange={() => toggleOne(p.id)} data-testid={`product-select-${p.sku}`} /></td>
                <td className="p-3 font-mono text-xs">{p.sku}{p.active === false && <span className="ml-1 text-[10px] font-bold uppercase text-slate-500" data-testid={`product-hidden-${p.sku}`}>hidden</span>}</td>
                <td className="p-3 max-w-xs"><div className="truncate">{p.name}{p.price_locked && <Lock size={12} className="inline ml-1 text-accent" />}</div><div className="text-[11px] text-muted-foreground truncate" data-testid={`product-category-${p.sku}`}>{p.category || "Uncategorised"}</div></td>
                <td className="p-3 text-xs text-muted-foreground">{p.brand || "—"}</td>
                <td className="p-3 text-right font-mono">{money(p.supplier_cost)}</td>
                <td className="p-3 text-right font-mono">{p.markup_percentage}%</td>
                <td className="p-3 text-right font-mono">{money(p.selling_ex_gst)}</td>
                <td className="p-3 text-right font-mono text-muted-foreground">{money(p.gst_amount)}</td>
                <td className="p-3 text-right font-mono font-bold text-accent">{money(p.final_customer_price)}</td>
                <td className="p-3 text-right font-mono text-emerald-600">{money(margin(p))}</td>
                <td className="p-3 text-center">{p.stock > 0
                  ? <Badge className="bg-emerald-600 hover:bg-emerald-600">{p.stock}</Badge>
                  : <Badge className="bg-slate-500 hover:bg-slate-500">0</Badge>}</td>
                <td className="p-3 text-right">
                  <Button data-testid={`edit-product-${p.sku}`} size="sm" variant="outline" onClick={() => setEdit({ ...p })}>Edit</Button>
                  <Button data-testid={`delete-product-${p.sku}`} size="sm" variant="outline" className="ml-1.5 text-slate-500 hover:text-red-600" title="Delete product" onClick={() => setDel(p)}><Trash2 size={14} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="p-6 text-center text-muted-foreground">{loading ? "Loading…" : "No products match. Change the filters, import a supplier CSV or add one manually."}</p>}
      </div>
      {total > per && <div className="mt-3"><Pager page={page} pages={pages} total={total} per={per} loading={loading} setPage={setPage} setPer={(n) => { setPer(n); setPage(0); }} bottom /></div>}

      {edit && <ProductDialog product={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {creating && <ProductDialog product={null} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      <ConfirmDelete open={!!del} title={`Delete ${del?.sku}?`} onConfirm={doDelete} onCancel={() => setDel(null)}
        description={`“${del?.name}” will be permanently removed from the store. This can't be undone. If a supplier import includes this SKU again, it will be re-created.`} />
    </div>
  );
}

const PER_PAGE = [25, 50, 100, 200, 500];

function FilterSelect({ value, onChange, placeholder, items, testId }) {
  return (
    <Select value={value || "all"} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="w-48 h-9"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="max-h-72">
        <SelectItem value="all">{placeholder}</SelectItem>
        {items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function Pager({ page, pages, total, per, loading, setPage, setPer, bottom }) {
  const t = bottom ? "-bottom" : "";
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm mb-2" data-testid={`products-pager${t}`}>
      <span className="text-muted-foreground" data-testid={`products-total${t}`}>{loading ? "Loading…" : `${total ? page * per + 1 : 0}–${Math.min(total, (page + 1) * per)} of ${total} products`}</span>
      <span className="ml-auto text-muted-foreground">Per page</span>
      <Select value={String(per)} onValueChange={(v) => setPer(Number(v))}>
        <SelectTrigger data-testid={`products-per-page${t}`} className="w-20 h-8"><SelectValue /></SelectTrigger>
        <SelectContent>{PER_PAGE.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
      </Select>
      <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid={`products-prev${t}`}>Prev</Button>
      <span data-testid={`products-page${t}`}>Page {page + 1} / {pages}</span>
      <Button size="sm" variant="outline" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)} data-testid={`products-next${t}`}>Next</Button>
    </div>
  );
}

export function ProductDialog({ product, onClose, onSaved }) {
  const isNew = !product;
  const [f, setF] = useState({
    sku: product?.sku || "", name: product?.name || "", description: product?.description || "",
    category: product?.category || "", brand: product?.brand || "", supplier: product?.supplier || "", image_url: product?.image_url || "",
    supplier_cost: product?.supplier_cost ?? 0, supplier_gst_treatment: product?.supplier_gst_treatment || "exclusive",
    markup_percentage: product?.markup_percentage ?? "", stock: product?.stock ?? 0, active: product?.active ?? true,
    price_locked: product?.price_locked || false, locked_price: product?.final_customer_price ?? 0,
    price_matched: product?.price_matched || false, competitor_price: product?.competitor_price ?? "",
    images: product?.images || (product?.image_url ? [product.image_url] : []),
    documents: product?.documents || [],
    shipping: product?.shipping || null,
    merchant: product?.merchant || {},
    media_locked: product?.media_locked || false,
  });
  const set = (k, v) => setF({ ...f, [k]: v });
  const [av, setAv] = useState(availabilityFrom(product));
  const [catOpts, setCatOpts] = useState([]);
  const [brandOpts, setBrandOpts] = useState([]);
  useEffect(() => {
    api.get("/admin/categories").then((r) => setCatOpts(r.data.map((c) => c.name))).catch(() => {});
    api.get("/admin/brands").then((r) => setBrandOpts(r.data.map((b) => b.name))).catch(() => {});
  }, []);
  const createOption = (coll, setOpts) => async (name) => {
    try {
      const { data } = await api.post(`/admin/${coll}`, { name });
      if (data.duplicate) toast.info(`“${data.item.name}” already exists — selected it`);
      else { setOpts((o) => [...o, data.item.name].sort()); toast.success(`Created “${data.item.name}”`); }
      return data.item.name;
    } catch { toast.error(`Could not create ${coll === "categories" ? "category" : "brand"}`); return null; }
  };
  const [uploading, setUploading] = useState(false);
  const doUpload = async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return data;
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); return null; }
    finally { setUploading(false); }
  };
  const onAddImages = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    const urls = [];
    for (const file of files) { const r = await doUpload(file, "image"); if (r) urls.push(r.url); }
    if (urls.length) setF((s) => {
      const images = [...s.images, ...urls];
      return { ...s, images, image_url: s.image_url || images[0] };
    });
  };
  const onAddDoc = async (e) => {
    const file = (e.target.files || [])[0]; e.target.value = "";
    if (!file) return;
    const r = await doUpload(file, "document");
    if (r) setF((s) => ({ ...s, documents: [...s.documents, { url: r.url, name: r.name }] }));
  };
  const removeImage = (i) => setF((s) => {
    const images = s.images.filter((_, idx) => idx !== i);
    return { ...s, images, image_url: images[0] || "" };
  });
  const makePrimary = (i) => setF((s) => {
    const images = [s.images[i], ...s.images.filter((_, idx) => idx !== i)];
    return { ...s, images, image_url: images[0] };
  });
  const removeDoc = (i) => setF((s) => ({ ...s, documents: s.documents.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!f.sku || !f.name) return toast.error("SKU and name are required");
    try {
      if (isNew) {
        await api.post("/admin/products", {
          sku: f.sku, name: f.name, description: f.description, category: f.category, brand: f.brand, image_url: f.image_url,
          images: f.images, documents: f.documents, media_locked: f.media_locked,
          supplier: f.supplier, supplier_cost: Number(f.supplier_cost), supplier_gst_treatment: f.supplier_gst_treatment,
          stock: Number(f.stock), markup_percentage: f.markup_percentage === "" ? null : Number(f.markup_percentage), active: f.active,
          shipping: f.shipping || undefined,
        });
        toast.success("Product created");
      } else {
        await api.put(`/admin/products/${product.id}`, {
          sku: f.sku, name: f.name, description: f.description, category: f.category, brand: f.brand, image_url: f.image_url,
          images: f.images, documents: f.documents, media_locked: f.media_locked,
          supplier: f.supplier, supplier_cost: Number(f.supplier_cost), supplier_gst_treatment: f.supplier_gst_treatment,
          stock: Number(f.stock), markup_percentage: f.markup_percentage === "" ? null : Number(f.markup_percentage), active: f.active,
          price_locked: f.price_locked, locked_price: f.price_locked ? Number(f.locked_price) : undefined,
          price_matched: f.price_matched, competitor_price: f.competitor_price === "" ? null : Number(f.competitor_price),
          ...(f.shipping ? { shipping: f.shipping } : {}),
          merchant: f.merchant,
          ...av,
        });
        toast.success("Product updated");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="product-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{isNew ? "Add Product" : product.name}</DialogTitle>
          <DialogDescription className="sr-only">Edit product details including category, brand, pricing and stock.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>SKU *</Label><Input data-testid="edit-sku" value={f.sku} onChange={(e) => set("sku", e.target.value)} /></div>
            <div><Label>Category</Label>
              <AjaxSelect testId="edit-category" value={f.category} onChange={(v) => set("category", v)}
                options={catOpts} placeholder="Select or create…" onCreate={createOption("categories", setCatOpts)} />
            </div>
          </div>
          <div><Label>Name *</Label><Input data-testid="edit-name" value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
          <div><Label>Brand</Label>
            <AjaxSelect testId="edit-brand" value={f.brand} onChange={(v) => set("brand", v)}
              options={brandOpts} placeholder="Select or create…" onCreate={createOption("brands", setBrandOpts)} />
          </div>
          <div><Label>Description</Label><Textarea data-testid="edit-description" rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Supplier</Label><Input data-testid="edit-supplier" value={f.supplier} onChange={(e) => set("supplier", e.target.value)} /></div>
            <div><Label>Supplier Prices</Label>
              <Select value={f.supplier_gst_treatment} onValueChange={(v) => set("supplier_gst_treatment", v)}>
                <SelectTrigger data-testid="edit-gst-treatment"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="exclusive">GST Exclusive</SelectItem><SelectItem value="inclusive">GST Inclusive</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Supplier Cost</Label><Input data-testid="edit-supplier-cost" type="number" value={f.supplier_cost} onChange={(e) => set("supplier_cost", e.target.value)} /></div>
            <div><Label>Markup % (blank = default)</Label><Input data-testid="edit-markup" type="number" value={f.markup_percentage} onChange={(e) => set("markup_percentage", e.target.value)} /></div>
            <div><Label>Stock (any level)</Label><Input data-testid="edit-stock" type="number" value={f.stock} onChange={(e) => set("stock", e.target.value)} /></div>
            <div className="col-span-2"><ProductShippingPanel value={f.shipping} onChange={(v) => set("shipping", v)} /></div>
            {!isNew && <div className="col-span-2"><ProductAvailabilityPanel value={av} onChange={setAv} supplierDefault={product?.supplier_backorder_default} /></div>}
            {!isNew && <div className="col-span-2"><ProductMerchantPanel value={f.merchant} onChange={(v) => set("merchant", v)} /></div>}
            <div className="flex items-end gap-2"><Switch data-testid="edit-active" checked={f.active} onCheckedChange={(c) => set("active", c)} /><Label>Active</Label></div>
          </div>
          <div className="flex gap-3 items-start">
            {f.image_url
              ? <img src={f.image_url} alt="" className="w-16 h-16 rounded-md object-cover border bg-slate-100" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              : <div className="w-16 h-16 rounded-md border bg-slate-100 grid place-items-center text-slate-300 text-[10px] text-center shrink-0">No image</div>}
            <div className="flex-1"><Label>Product Image URL</Label>
              <Input data-testid="edit-image-url" value={f.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="https://…" /></div>
          </div>
          <div className="rounded-lg border p-3 bg-slate-50/60 space-y-3" data-testid="media-section">
            <div className="flex items-center justify-between">
              <Label className="font-semibold">Product images</Label>
              <label className="flex items-center gap-1.5 text-sm text-accent font-semibold cursor-pointer hover:underline" data-testid="add-images-btn">
                <Upload size={15} /> {uploading ? "Uploading…" : "Upload images"}
                <input type="file" accept="image/*" multiple className="hidden" onChange={onAddImages} disabled={uploading} />
              </label>
            </div>
            {f.images.length === 0 ? (
              <div className="text-xs text-muted-foreground flex items-center gap-2"><ImageOff size={14} /> No images yet. Upload one or more — the first is the primary image.</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {f.images.map((img, i) => (
                  <div key={i} data-testid={`media-image-${i}`} className="relative w-20 h-20 rounded-md overflow-hidden border bg-white group">
                    <img src={img} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    {i === 0 && <span className="absolute bottom-0 inset-x-0 bg-accent text-steel text-[9px] text-center font-bold">PRIMARY</span>}
                    <button type="button" onClick={() => removeImage(i)} data-testid={`remove-image-${i}`}
                      className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100"><X size={12} /></button>
                    {i !== 0 && <button type="button" onClick={() => makePrimary(i)} title="Make primary"
                      className="absolute top-0.5 left-0.5 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100"><Star size={12} /></button>}
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between pt-1 border-t">
              <Label className="font-semibold">Documents (PDF/spec sheets)</Label>
              <label className="flex items-center gap-1.5 text-sm text-accent font-semibold cursor-pointer hover:underline" data-testid="add-doc-btn">
                <Upload size={15} /> Upload document
                <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" className="hidden" onChange={onAddDoc} disabled={uploading} />
              </label>
            </div>
            {f.documents.length > 0 && (
              <div className="space-y-1.5">
                {f.documents.map((d, i) => (
                  <div key={i} data-testid={`media-doc-${i}`} className="flex items-center gap-2 text-sm bg-white border rounded-md px-2 py-1.5">
                    <FileText size={15} className="text-accent shrink-0" />
                    <span className="truncate flex-1">{d.name || d.url}</span>
                    <button type="button" onClick={() => removeDoc(i)} data-testid={`remove-doc-${i}`} className="text-slate-400 hover:text-red-500"><X size={14} /></button>
                  </div>
                ))}
              </div>
            )}
            <label className="flex items-center gap-2 pt-1 border-t">
              <Switch data-testid="edit-media-locked" checked={f.media_locked} onCheckedChange={(c) => set("media_locked", c)} />
              <span className="text-sm">Lock media — supplier imports won't overwrite these images</span>
            </label>
          </div>
          {!isNew && (
            <div className="rounded-lg border p-3 bg-red-50/40">
              <label className="flex items-center gap-2 mb-2"><Switch data-testid="edit-price-match" checked={f.price_matched} onCheckedChange={(c) => set("price_matched", c)} />
                <span className="text-sm font-medium">Competitor price match ($1 under competitor)</span></label>
              {f.price_matched && (
                <div><Label>Competitor Price (incl GST)</Label>
                  <Input data-testid="edit-competitor-price" type="number" value={f.competitor_price} onChange={(e) => set("competitor_price", e.target.value)} />
                  <p className="text-xs text-muted-foreground mt-1">ACP price becomes this minus $1.00. Normal price shown as a strike-through saving.</p></div>
              )}
            </div>
          )}
          {!isNew && (
            <div className="rounded-lg border p-3 bg-amber-50/40">
              <label className="flex items-center gap-2 mb-2"><Switch data-testid="edit-price-lock" checked={f.price_locked} onCheckedChange={(c) => set("price_locked", c)} />
                <span className="text-sm font-medium">Lock &amp; manually override selling price</span></label>
              {f.price_locked && (
                <div><Label>Locked Final Price (incl GST)</Label>
                  <Input data-testid="edit-locked-price" type="number" value={f.locked_price} onChange={(e) => set("locked_price", e.target.value)} />
                  <p className="text-xs text-muted-foreground mt-1">Locked products are not overwritten by supplier CSV imports.</p></div>
              )}
            </div>
          )}
          <Button data-testid="save-product" onClick={save} className="w-full bg-steel text-white hover:bg-steel-light">{isNew ? "Create Product" : "Save Changes"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
